import { useMemo, useRef, useState } from "react";
import { useLocale } from "@/hooks/use-locale";
import { useTranslation } from "react-i18next";
import {
  Badge,
  ChipList,
  MultiSelect,
  SearchInput,
  SortableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableStateRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@h2j-data-team/ui";
import type { AnalysisField, AnalysisObject } from "@repo/shared";
import { DensityToggle } from "@/components/density-toggle";
import { useAnalysisTableFilters, useAnalysisTableSort } from "@/lib/use-analysis-view-preferences";
import { useTableDensity } from "@/lib/use-table-density";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { formatBytes } from "@/lib/health-format";
import { AnalysisTableCard, ExpandableRow, FlagBadge, SEARCH_CLASS } from "./analysis-table";
import { usePickerMessages } from "./use-picker-messages";
import { UsageRefBadges } from "./UsageRefBadges";
import { useSheetTitles } from "./use-sheet-titles";

type FilterMode = "used" | "unused" | "indirect" | "keyText";
type FieldsSortCol =
  | "name"
  | "byteSize"
  | "indexByteSize"
  | "totalByteSize"
  | "cardinality"
  | "usageCount";

type FieldsFilterState = {
  filter: FilterMode[];
  search: string;
  tags: string[];
};

const FIELDS_FILTER_DEFAULTS: FieldsFilterState = {
  filter: [],
  search: "",
  tags: [],
};

const COLUMN_COUNT = 9;

interface FieldsAnalysisTableProps {
  analysisId: string;
  fields: AnalysisField[];
  objects: AnalysisObject[];
}

function isUnusedField(field: AnalysisField): boolean {
  return field.usageCount === 0 && !field.isKey;
}

function isUsedField(field: AnalysisField): boolean {
  return !isUnusedField(field);
}

export function FieldsAnalysisTable({ analysisId, fields, objects }: FieldsAnalysisTableProps) {
  const { language } = useLocale();
  const { t } = useTranslation("analyses");
  const { t: tc } = useTranslation("common");
  const pickerMessages = usePickerMessages();
  const { density, setDensity } = useTableDensity();
  const { filters, setFilters } = useAnalysisTableFilters<FieldsFilterState>(
    analysisId,
    "fields",
    FIELDS_FILTER_DEFAULTS,
  );
  const filter = useMemo(
    () => (Array.isArray(filters.filter) ? filters.filter : []),
    [filters.filter],
  );
  const { search, tags } = filters;
  const { sortBy, sortDir, setSort } = useAnalysisTableSort<FieldsSortCol>("fields", {
    sortBy: "totalByteSize",
    sortDir: "desc",
  });
  const [expandedField, setExpandedField] = useState<string | null>(null);
  const sheetTitles = useSheetTitles(objects);
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);

  const tagOptions = useMemo(() => {
    const all = new Set<string>();
    for (const f of fields) for (const tag of f.tags) all.add(tag);
    return Array.from(all)
      .sort()
      .map((tag) => ({ label: tag, value: tag }));
  }, [fields]);

  const selectedTags = useMemo(() => {
    const available = new Set(tagOptions.map((o) => o.value));
    return tags.filter((tag) => available.has(tag));
  }, [tags, tagOptions]);

  const filtered = useMemo(() => {
    let result = fields;
    if (filter.length > 0) {
      result = result.filter((f) =>
        filter.some((mode) => {
          if (mode === "used") return isUsedField(f);
          if (mode === "unused") return isUnusedField(f);
          if (mode === "indirect") return f.isIndirectUsage;
          return f.isKeyText;
        }),
      );
    }
    if (selectedTags.length > 0) {
      result = result.filter((f) => selectedTags.every((tag) => f.tags.includes(tag)));
    }
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (f) =>
          f.name.toLowerCase().includes(lower) ||
          f.tableNames.some((name) => name.toLowerCase().includes(lower)),
      );
    }
    result = [...result].sort((a, b) => {
      const aVal = a[sortBy] ?? 0;
      const bVal = b[sortBy] ?? 0;
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === "asc"
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
    return result;
  }, [fields, filter, selectedTags, search, sortBy, sortDir]);

  const handleSort = (col: FieldsSortCol) => {
    if (sortBy === col) {
      setSort(col, sortDir === "asc" ? "desc" : "asc");
    } else {
      setSort(col, col === "name" ? "asc" : "desc");
    }
  };

  const direction = (col: FieldsSortCol) => (sortBy === col ? sortDir : undefined);

  const filterOptions = (["used", "unused", "indirect", "keyText"] as const)
    .map((mode) => ({
      mode,
      count:
        mode === "used"
          ? fields.filter(isUsedField).length
          : mode === "unused"
            ? fields.filter(isUnusedField).length
            : mode === "indirect"
              ? fields.filter((f) => f.isIndirectUsage).length
              : fields.filter((f) => f.isKeyText).length,
    }))
    .filter(({ mode, count }) => !((mode === "indirect" || mode === "keyText") && count === 0))
    .map(({ mode, count }) => ({
      value: mode,
      label: t(`fields.filter${mode[0].toUpperCase()}${mode.slice(1)}`, { count }),
    }));
  const selectedFilters = filterOptions.filter((o) => filter.includes(o.value)).map((o) => o.value);

  return (
    <AnalysisTableCard
      toolbar={
        <>
          <SearchInput
            ref={searchRef}
            containerClassName={SEARCH_CLASS}
            aria-label={t("fields.searchPlaceholder")}
            placeholder={t("fields.searchPlaceholder")}
            clearLabel={tc("list.clearSearch")}
            shortcutHint="/"
            value={search}
            onValueChange={(value) => setFilters({ search: value })}
          />
          <MultiSelect
            size="compact"
            className="w-52"
            aria-label={t("filterByUsage")}
            options={filterOptions}
            value={selectedFilters}
            onValueChange={(next) => setFilters({ filter: next })}
            messages={pickerMessages(t("filterByUsage"))}
            maxDisplay={1}
          />
          <MultiSelect
            size="compact"
            className="w-56"
            aria-label={t("fields.tagsPlaceholder")}
            options={tagOptions}
            value={selectedTags}
            onValueChange={(next) => setFilters({ tags: next })}
            messages={pickerMessages(t("fields.tagsPlaceholder"), t("fields.noTagsFound"))}
            maxDisplay={1}
          />
          <DensityToggle value={density} onChange={setDensity} />
        </>
      }
      footer={
        fields.length > 0 &&
        t("fields.showing", { filtered: filtered.length, total: fields.length })
      }
    >
      <Table
        className="min-w-[1100px]"
        scrollLabel={t("detail.tabs.fields")}
        scrollClassName="min-h-0 flex-1"
      >
        <TableHeader>
          <TableRow>
            <TableHead className="w-10 px-2">
              <span className="sr-only">{t("table.details")}</span>
            </TableHead>
            <SortableHead direction={direction("name")} onSort={() => handleSort("name")}>
              {t("fields.colField")}
            </SortableHead>
            <TableHead>{t("fields.colTable")}</TableHead>
            <SortableHead
              className="w-[142px]"
              align="end"
              direction={direction("cardinality")}
              onSort={() => handleSort("cardinality")}
            >
              {t("fields.colCardinality")}
            </SortableHead>
            <Tooltip>
              <TooltipTrigger asChild>
                <SortableHead
                  className="w-[100px]"
                  align="end"
                  direction={direction("byteSize")}
                  onSort={() => handleSort("byteSize")}
                >
                  {t("fields.colSymbol")}
                </SortableHead>
              </TooltipTrigger>
              <TooltipContent>{t("fields.tooltipSymbol")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <SortableHead
                  className="w-[100px]"
                  align="end"
                  direction={direction("indexByteSize")}
                  onSort={() => handleSort("indexByteSize")}
                >
                  {t("fields.colIndex")}
                </SortableHead>
              </TooltipTrigger>
              <TooltipContent>{t("fields.tooltipIndex")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <SortableHead
                  className="w-[100px]"
                  align="end"
                  direction={direction("totalByteSize")}
                  onSort={() => handleSort("totalByteSize")}
                >
                  {t("fields.colTotal")}
                </SortableHead>
              </TooltipTrigger>
              <TooltipContent>{t("fields.tooltipTotal")}</TooltipContent>
            </Tooltip>
            <TableHead className="w-[210px]">{t("fields.colTags")}</TableHead>
            <SortableHead
              className="w-[190px]"
              align="end"
              direction={direction("usageCount")}
              onSort={() => handleSort("usageCount")}
            >
              {t("fields.colUsage")}
            </SortableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableStateRow colSpan={COLUMN_COUNT} state="no-results" title={t("fields.noMatch")} />
          )}
          {filtered.map((field) => (
            <ExpandableRow
              key={field.name}
              name={field.name}
              expandable={field.usedIn.length > 0}
              expanded={expandedField === field.name}
              onToggle={() => setExpandedField(expandedField === field.name ? null : field.name)}
              colSpan={COLUMN_COUNT}
              detail={
                <>
                  <p className="mb-2 text-xs font-medium text-dim">{t("fields.usedIn")}</p>
                  <UsageRefBadges refs={field.usedIn} sheetTitles={sheetTitles} showContext />
                </>
              }
            >
              <TableCell wrap className="font-mono font-medium">
                {field.name}
              </TableCell>
              <TableCell wrap className="font-mono text-dim">
                {field.tableNames.length > 1 ? field.tableNames.join(", ") : field.tableName}
              </TableCell>
              <TableCell align="end" variant="numeric">
                {field.cardinality.toLocaleString(language)}
              </TableCell>
              <TableCell align="end" variant="numeric" className="text-dim">
                {formatBytes(field.byteSize)}
              </TableCell>
              <TableCell align="end" variant="numeric" className="text-dim">
                {formatBytes(field.indexByteSize)}
              </TableCell>
              <TableCell align="end" variant="numeric" className="font-medium">
                {formatBytes(field.totalByteSize)}
              </TableCell>
              <TableCell>
                <ChipList
                  aria-label={t("fields.colTags")}
                  items={field.tags.map((tag) => ({ id: tag, label: tag }))}
                  max={2}
                  overflowLabel={(count) => t("fields.moreTags", { count })}
                  collapseLabel={t("fields.fewerTags")}
                />
              </TableCell>
              <TableCell align="end">
                <div className="flex flex-nowrap items-center justify-end gap-1">
                  {field.isKeyText && (
                    <FlagBadge
                      variant="warning"
                      label={t("fields.keyTextBadge")}
                      hint={t("fields.keyTextTooltip")}
                    />
                  )}
                  {field.isIndirectUsage && (
                    <FlagBadge
                      label={t("fields.indirectBadge")}
                      hint={t("fields.indirectTooltip")}
                    />
                  )}
                  {field.isOrphanUsageOnly && (
                    <FlagBadge label={t("fields.orphanBadge")} hint={t("fields.orphanTooltip")} />
                  )}
                  {field.usedIn.length > 0 ? (
                    <Badge variant="default" className="font-mono tabular-nums">
                      {field.usageCount}
                    </Badge>
                  ) : field.isKey ? (
                    <Badge variant="secondary" className="font-mono">
                      $key
                    </Badge>
                  ) : (
                    <Badge variant="warning">{t("fields.unusedBadge")}</Badge>
                  )}
                </div>
              </TableCell>
            </ExpandableRow>
          ))}
        </TableBody>
      </Table>
    </AnalysisTableCard>
  );
}
